/**
 * Wave 10 (Frontend audit H1): single source of truth for parsing
 * the vendor portal's `shop_slug` from the URL.
 *
 * Pre-fix this logic was duplicated in `Login.tsx:39-42` and
 * `Register.tsx:53-56`. Any drift between them would silently break
 * one but not the other — exactly the class of bug we want gone.
 *
 * Convention: SPA basename is `/vendors`. The URL `/vendors/<slug>/login`
 * surfaces `<slug>` as `pathname.split('/').filter(Boolean)[1]`.
 * Anything that matches a known SPA route (login/register/etc.) is
 * NOT a slug — return empty string so the caller can render a
 * "no marketplace selected" fallback.
 *
 * Wave-66: also exposes `rememberShopSlug` / `getRememberedShopSlug`
 * so a returning vendor who lands on `/login` (without the marketplace
 * slug in the URL) is auto-recovered to `/<remembered>/login` rather
 * than being shown the unhelpful "use the marketplace-specific URL"
 * toast. The remembered slug is written on every successful login or
 * register so it tracks the most-recent marketplace.
 */

const KNOWN_ROUTES = new Set([
  'login',
  'register',
  'forgot-password',
  'reset-password',
  'verify-email',
  'auth',
  'dashboard',
]);

const REMEMBERED_SLUG_KEY = 'vendoriq:last_shop_slug';

const SHOP_SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/;
const UNSAFE_INTERNAL_PATH = /[\\\u0000-\u001f\u007f]|%(?:00|0a|0d|5c)/i;

/** Strict boundary for slugs read from URLs, API responses, or storage. */
export function normalizeShopSlug(value: unknown): string {
  if (typeof value !== 'string' || value.length === 0 || value.length > 64) return '';
  if (value !== value.trim()) return '';
  const normalized = value.toLowerCase();
  if (!SHOP_SLUG_RE.test(normalized) || KNOWN_ROUTES.has(normalized)) return '';
  return normalized;
}

/** Constrain React Router destinations to a same-origin absolute path. */
export function normalizeInternalPath(value: unknown, fallback = '/dashboard'): string {
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//')) {
    return fallback;
  }
  if (UNSAFE_INTERNAL_PATH.test(value)) return fallback;
  try {
    const base = new URL('https://vendoriq.invalid');
    const parsed = new URL(value, base);
    if (parsed.origin !== base.origin) return fallback;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return fallback;
  }
}

export function resolveShopSlugFromUrl(): string {
  if (typeof window === 'undefined') return '';
  const segments = window.location.pathname.split('/').filter(Boolean);
  const afterBase = segments[0] === 'vendors' ? segments.slice(1) : segments;
  return normalizeShopSlug(afterBase[0]);
}

/**
 * Persist the marketplace slug the vendor most recently authenticated
 * against. Called from AuthContext on login + register success. Cheap
 * (one localStorage write) and the value is non-PII (it's the public
 * portal slug a vendor would type into the URL bar anyway).
 */
export function rememberShopSlug(slug: string): void {
  if (typeof window === 'undefined') return;
  const normalized = normalizeShopSlug(slug);
  if (!normalized) return;
  // Sanity check — never persist a known-route value (paranoia: a stray
  // call site passing `window.location.pathname` etc.).
  try {
    window.localStorage.setItem(REMEMBERED_SLUG_KEY, normalized);
  } catch {
    // localStorage can throw in private-browsing modes — non-fatal.
  }
}

/**
 * Returns the previously-remembered marketplace slug, or empty string
 * if none. Used by Login.tsx to auto-redirect a returning vendor who
 * lands on the bare `/login` URL.
 */
export function getRememberedShopSlug(): string {
  if (typeof window === 'undefined') return '';
  try {
    const remembered = window.localStorage.getItem(REMEMBERED_SLUG_KEY) || '';
    const normalized = normalizeShopSlug(remembered);
    if (!normalized && remembered) {
      window.localStorage.removeItem(REMEMBERED_SLUG_KEY);
    }
    return normalized;
  } catch {
    return '';
  }
}

/** Clear the remembered slug — call on explicit logout. */
export function clearRememberedShopSlug(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(REMEMBERED_SLUG_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Wave-67: best-effort marketplace slug for building auth-page URLs.
 * Used by every internal redirect site (ProtectedRoute bounce,
 * /forgot-password "back" link, axios 401 interceptor, logout, etc.)
 * so the user never lands on the slug-less generic `/login` form.
 *
 * Resolution order:
 *   1. Slug already in the current URL (we're at /:shop_slug/something)
 *   2. localStorage-persisted slug from a prior successful auth
 *   3. Empty string — caller falls back to the bare `/login` route
 *      which still works (the amber "No marketplace selected" banner
 *      renders for first-time-ever visits with no memory).
 */
export function getBestShopSlug(): string {
  const fromUrl = resolveShopSlugFromUrl();
  if (fromUrl) return fromUrl;
  return getRememberedShopSlug();
}

/**
 * Wave-67: build an auth page path that preserves the current
 * marketplace context. Pass a leaf like "login", "register",
 * "forgot-password" — get back "/<slug>/<leaf>" if a slug is
 * resolvable, else "/<leaf>" as a final fallback.
 *
 * Example:
 *   authPath('login')          -> "/kernvel-vendoriq/login"
 *   authPath('forgot-password') -> "/kernvel-vendoriq/forgot-password"
 *
 * The leading "/" is included so callers can drop the result
 * directly into <Navigate to={...}>, useNavigate(), or Link to=.
 */
export function authPath(leaf: 'login' | 'register' | 'forgot-password' | 'reset-password' | 'verify-email'): string {
  const slug = getBestShopSlug();
  return slug ? `/${slug}/${leaf}` : `/${leaf}`;
}

/**
 * Wave-69: build a protected-page path that includes the marketplace
 * slug. Every authenticated route lives under `/:shop_slug/*` so we
 * need to stitch the slug back onto navigations from event handlers
 * (which often want to jump cross-tree, e.g. "go to /products/new").
 *
 * Pass anything starting with `/` (an absolute path) — get back
 * `/<slug>/<rest>`. If no slug is resolvable, returns the input
 * unchanged so SlugRecover catches it as a fallback.
 *
 * Examples:
 *   scopedPath('/dashboard')        -> "/kernvel-vendoriq/dashboard"
 *   scopedPath('/products/new')     -> "/kernvel-vendoriq/products/new"
 *   scopedPath('/orders/abc-123')   -> "/kernvel-vendoriq/orders/abc-123"
 *   scopedPath('dashboard')         -> "dashboard"  (relative — caller knows what they're doing)
 */
export function scopedPath(absolutePath: string): string {
  const slug = getBestShopSlug();
  const fallback = slug ? `/${slug}/dashboard` : '/dashboard';
  const safePath = normalizeInternalPath(absolutePath, fallback);
  if (!slug) return safePath;
  // Avoid double-slugging — if the path already starts with /<slug>/,
  // leave it alone.
  if (safePath === `/${slug}` || safePath.startsWith(`/${slug}/`)) {
    return safePath;
  }
  return `/${slug}${safePath}`;
}
